import { useRef } from "react";
import { useNavigate } from "react-router";
import { Chat_1Icon } from "vega-icons";

import { type ConversationResponse } from "@vega-api/generated/types/conversationResponse";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { NotificationCenterLoadMoreSentinel } from "@vega-core/components/NotificationCenter/components/NotificationCenterLoadMoreSentinel";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@vega-core/components/shadcn/popover";
import { Tag, TagVariants } from "@vega-core/components/Tag/Tag";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";
import { CoInvestmentFullPaths } from "@vega-modules/paths";
import { CoInvestBadgeTriggerButton } from "@vega-modules/scenes/CoInvestments/components/CoInvestBadgeTriggerButton/CoInvestBadgeTriggerButton";
import {
  useInfiniteConversations,
  useUnreadMessagesTotal,
} from "@vega-modules/scenes/CoInvestments/messaging/conversationsData";
import { MessagingErrorState } from "@vega-modules/scenes/CoInvestments/messaging/MessagingErrorState";

const PREVIEW_PAGE_SIZE = 10;

type Props = {
  dealId: string;
};

export const CoInvestDealMessagesPopover = ({ dealId }: Props) => {
  const { value: isOpen, setValue: setOpen } = useBoolean(false);
  const navigate = useNavigate();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const previewsQuery = useInfiniteConversations({
    metaFilter: { dealId },
    pageSize: PREVIEW_PAGE_SIZE,
    enabled: isOpen,
  });
  const previewConversations =
    previewsQuery.data?.pages.flatMap((page) => page.data) ?? [];

  const unreadCount = useUnreadMessagesTotal({ dealId });

  const goToConversation = (conversation: ConversationResponse) => {
    setOpen(false);
    navigate(
      CoInvestmentFullPaths.CoInvestmentConversation.replace(
        ":conversationId",
        conversation.id,
      ),
    );
  };

  const goToMessages = () => {
    setOpen(false);
    navigate(CoInvestmentFullPaths.CoInvestmentMessages);
  };

  return (
    <Popover onOpenChange={setOpen} open={isOpen}>
      <PopoverTrigger asChild>
        <CoInvestBadgeTriggerButton
          aria-label={m.coInvestments_messages_openAriaLabel()}
          count={unreadCount}
          dataTestId="coinvest-deal-messages-trigger"
          icon={<Chat_1Icon aria-hidden="true" className="h-5 w-5" />}
        />
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="max-h-64 overflow-y-auto" ref={scrollContainerRef}>
          {previewsQuery.isError && previewConversations.length === 0 ? (
            <MessagingErrorState
              isRetrying={previewsQuery.isRefetching}
              onRetry={() => previewsQuery.refetch()}
              testId="coinvest-deal-messages-error"
            />
          ) : null}
          {previewsQuery.isLoading ? (
            <div
              className="flex flex-col gap-2 p-4"
              data-testid="coinvest-deal-messages-loading"
            >
              <div className="skeleton-box h-10 rounded-lg" />
              <div className="skeleton-box h-10 rounded-lg" />
              <div className="skeleton-box h-10 rounded-lg" />
            </div>
          ) : null}
          <ul data-testid="coinvest-deal-messages-previews">
            {previewConversations.map((conversation) => {
              const isUnread = conversation.unreadCount > 0;

              return (
                <li
                  className="border-b border-border/primary"
                  key={conversation.id}
                >
                  <button
                    className="w-full p-4 text-left hover:bg-background/secondary defaultTransition flex flex-wrap items-center gap-2"
                    data-unread={isUnread ? "true" : undefined}
                    onClick={() => goToConversation(conversation)}
                    type="button"
                  >
                    <span className="flex items-center gap-2">
                      {isUnread ? (
                        <span
                          aria-hidden="true"
                          className="shrink-0 h-1.5 w-1.5 rounded-full bg-brand-default"
                        />
                      ) : null}
                      <Tag variant={TagVariants.Disabled}>
                        {conversation.meta.limitedPartnerName}
                      </Tag>
                    </span>
                    <p
                      className={cnTwMerge(
                        "body-base-regular text-text/primary line-clamp-2",
                        isUnread && "body-base-semibold",
                      )}
                    >
                      {conversation.latestMessage?.messageContent}
                    </p>
                  </button>
                </li>
              );
            })}
          </ul>
          {previewsQuery.hasNextPage ? (
            <NotificationCenterLoadMoreSentinel
              isLoadingMore={previewsQuery.isFetchingNextPage}
              loadingLabel={m.common_loading()}
              onLoadMore={() => previewsQuery.fetchNextPage()}
              scrollRootRef={scrollContainerRef}
            />
          ) : null}
        </div>
        <div className="p-3 text-center">
          <ThemedButton
            dataTestId="coinvest-deal-messages-go-to-messages"
            onClick={goToMessages}
            variant="text"
          >
            {m.coInvestments_messages_goToMessages()}
          </ThemedButton>
        </div>
      </PopoverContent>
    </Popover>
  );
};
